import { 
  User, Mail, Phone, ChevronDown, Building, CreditCard, Map, 
  MapPin, Hash, Calendar, Globe 
} from 'lucide-react';
import CitySelect from './CitySelect';

// Lista de estados brasileiros
  const brazilStates = [
    { value: 'AC', label: 'Acre' },
    { value: 'AL', label: 'Alagoas' },
    { value: 'AP', label: 'Amapá' },
    { value: 'AM', label: 'Amazonas' },
    { value: 'BA', label: 'Bahia' },
    { value: 'CE', label: 'Ceará' },
    { value: 'DF', label: 'Distrito Federal' },
    { value: 'ES', label: 'Espírito Santo' },
    { value: 'GO', label: 'Goiás' },
    { value: 'MA', label: 'Maranhão' },
    { value: 'MT', label: 'Mato Grosso' },
    { value: 'MS', label: 'Mato Grosso do Sul' },
    { value: 'MG', label: 'Minas Gerais' },
    { value: 'PA', label: 'Pará' },
    { value: 'PB', label: 'Paraíba' },
    { value: 'PR', label: 'Paraná' },
    { value: 'PE', label: 'Pernambuco' },
    { value: 'PI', label: 'Piauí' },
    { value: 'RJ', label: 'Rio de Janeiro' },
    { value: 'RN', label: 'Rio Grande do Norte' },
    { value: 'RS', label: 'Rio Grande do Sul' },
    { value: 'RO', label: 'Rondônia' },
    { value: 'RR', label: 'Roraima' },
    { value: 'SC', label: 'Santa Catarina' },
    { value: 'SP', label: 'São Paulo' },
    { value: 'SE', label: 'Sergipe' },
    { value: 'TO', label: 'Tocantins' }
  ];

const FormInputRenderer = ({ 
  input, 
  value, 
  onChange, 
  error, 
  primaryColor,
  stateValue // Para filtrar cidades por estado
}) => {
  const baseInputClass = "w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-all text-xs";
  const errorClass = error ? "border-red-300" : "border-gray-200";

  const inputStyle = {
    '--tw-ring-color': primaryColor
  };

  switch (input.type) {

    // --- TEXT / NAME ---
    case 'text':
    case 'name':
      return (
        <div className="relative">
          <User className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={input.placeholder || input.label}
            className={`${baseInputClass} ${errorClass} pl-11`}
            style={inputStyle}
          />
        </div>
      );

    // --- EMAIL ---
    case 'email':
      return (
        <div className="relative">
          <Mail className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
          <input
            type="email"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={input.placeholder || input.label}
            className={`${baseInputClass} ${errorClass} pl-11`}
            style={inputStyle}
          />
        </div>
      );

    // --- PHONE ---
    case 'phone':
      // Função para formatar o telefone
      const formatPhone = (value) => {
        // Remove tudo que não é dígito
        const numbers = value.replace(/\D/g, '');
        
        // Aplica a máscara (XX) XXXXX-XXXX
        if (numbers.length <= 2) {
          return numbers;
        } else if (numbers.length <= 7) {
          return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
        } else if (numbers.length <= 11) {
          return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
        }
        // Limita a 11 dígitos
        return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
      };

      return (
        <div className="relative">
          <Phone className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
          <input
            type="tel"
            value={value || ''}
            onChange={(e) => {
              const formatted = formatPhone(e.target.value);
              onChange(formatted);
            }}
            placeholder={input.placeholder || '(00) 00000-0000'}
            maxLength={15} // (XX) XXXXX-XXXX = 15 caracteres
            className={`${baseInputClass} ${errorClass} pl-11`}
            style={inputStyle}
          />
        </div>
      );

    // --- COMPANY ---
    case 'company':
      return (
        <div className="relative">
          <Building className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={input.placeholder || input.label}
            className={`${baseInputClass} ${errorClass} pl-11`}
            style={inputStyle}
          />
        </div>
      );

    // --- CPF ---
    case 'cpf':
      return (
        <div className="relative">
          <CreditCard className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
          <input
            type="text"
            inputMode="numeric"
            maxLength={14}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={input.placeholder || input.label}
            className={`${baseInputClass} ${errorClass} pl-11`}
            style={inputStyle}
          />
        </div>
      );

    // --- STATE ---
    case 'state':
      return (
        <div className="relative">
          <select
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className={`${baseInputClass} ${errorClass} appearance-none pr-10 bg-white text-gray-900`}
            style={inputStyle}
          >
            <option value="">{input.placeholder || 'Selecione o estado'}</option>
            {brazilStates.map((state) => (
              <option key={state.value} value={state.value} className="text-gray-900 bg-white">
                {state.label}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-3.5 w-5 h-5 text-gray-400 pointer-events-none" />
        </div>
      );

    // --- CITY ---
    case 'city':
      return (
        <CitySelect
          value={value || ''}
          onChange={onChange}
          placeholder={input.placeholder || 'Selecione a cidade'}
          filterByState={stateValue}
        />
      );

    // --- DROPDOWN PERSONALIZADO ---
    case 'dropdown':
      return (
        <div className="relative">
          <select
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className={`${baseInputClass} ${errorClass} appearance-none pr-10`}
            style={inputStyle}
          >
            <option value="">{input.placeholder || `Selecione ${input.label}`}</option>
            {input.options?.map((option, idx) => (
              <option className="text-gray-900" key={idx} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-3.5 w-5 h-5 text-gray-400 pointer-events-none" />
        </div>
      );

    // --- NUMBER ---
    case 'number':
      return (
        <div className="relative">
          <Hash className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
          <input
            type="number"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={input.placeholder || input.label}
            className={`${baseInputClass} ${errorClass} pl-11`}
            style={inputStyle}
          />
        </div>
      );

    // --- DATE ---
    case 'date':
      return (
        <div className="relative">
          <Calendar className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
          <input
            type="date"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className={`${baseInputClass} ${errorClass} pl-11`}
            style={inputStyle}
          />
        </div>
      );

    // --- URL ---
    case 'url':
      return (
        <div className="relative">
          <Globe className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
          <input
            type="url"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={input.placeholder || 'https://exemplo.com'}
            className={`${baseInputClass} ${errorClass} pl-11`}
            style={inputStyle}
          />
        </div>
      );

    default:
      return null;
  }
};

export default FormInputRenderer;
